import { ref, computed, inject, provide, onMounted, onUnmounted } from 'vue';
import emitter from '../../../../common/utilities/emitter.js';

export const CURSOR_STATE_KEY = Symbol('cursorState');

/**
 * Sets up and provides shared cursor state to child cursor components.
 * Must be called in the parent (CursorBase) that wraps a specific cursor component.
 */
export function provideCursorState() {
  const x = ref(0);
  const y = ref(0);
  const isHovering = ref(false);
  const isProjectHovering = ref(false);
  const isOverIframe = ref(false);
  const isOutsideWindow = ref(false);

  const currentState = computed(() => {
    if (isProjectHovering.value) return 'project';
    if (isHovering.value) return 'hover';
    return 'default';
  });

  const containerStyle = computed(() => ({
    transform: `translate3d(${x.value}px, ${y.value}px, 0)`,
  }));

  // -------------------------------------------------------------------------
  // Mouse tracking
  // -------------------------------------------------------------------------

  function updateMouse(e) {
    x.value = e.clientX;
    y.value = e.clientY;
    isOutsideWindow.value = false;

    const interactiveEl = e.target.closest(
      'a, button, .btn, [role="button"], input, select, textarea, .clickable'
    );
    isHovering.value = !!interactiveEl;
  }

  // -------------------------------------------------------------------------
  // Window exit/enter detection
  // mouseout on documentElement fires when the pointer leaves the viewport —
  // relatedTarget is null when the pointer truly exited (not just moved between
  // elements). This works during drags unlike mouseleave on document.
  // -------------------------------------------------------------------------

  function onDocumentMouseOut(e) {
    if (e.relatedTarget === null) isOutsideWindow.value = true;
  }

  function onDocumentMouseOver() {
    isOutsideWindow.value = false;
  }

  function setProjectHover(val) {
    isProjectHovering.value = val;
  }

  // -------------------------------------------------------------------------
  // iframe awareness
  // -------------------------------------------------------------------------

  const iframeSet = new Set();
  let iframeMutationObserver = null;

  function onIframeEnter() { isOverIframe.value = true; }
  function onIframeLeave() { isOverIframe.value = false; }

  function attachIframe(iframe) {
    if (iframeSet.has(iframe)) return;
    iframe.addEventListener('mouseenter', onIframeEnter);
    iframe.addEventListener('mouseleave', onIframeLeave);
    iframeSet.add(iframe);
  }

  function detachIframe(iframe) {
    iframe.removeEventListener('mouseenter', onIframeEnter);
    iframe.removeEventListener('mouseleave', onIframeLeave);
    iframeSet.delete(iframe);
  }

  function attachAllIframes() {
    document.querySelectorAll('iframe').forEach(attachIframe);
  }

  function detachAllIframes() {
    iframeSet.forEach(detachIframe);
    iframeSet.clear();
  }

  function startIframeObserver() {
    iframeMutationObserver = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach(node => {
          if (node.nodeName === 'IFRAME') attachIframe(node);
          if (node.querySelectorAll) node.querySelectorAll('iframe').forEach(attachIframe);
        });
        mutation.removedNodes.forEach(node => {
          if (node.nodeName === 'IFRAME') detachIframe(node);
          if (node.querySelectorAll) node.querySelectorAll('iframe').forEach(detachIframe);
        });
      }
    });
    iframeMutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  onMounted(() => {
    window.addEventListener('mousemove', updateMouse);
    document.documentElement.addEventListener('mouseout',  onDocumentMouseOut);
    document.documentElement.addEventListener('mouseover', onDocumentMouseOver);
    emitter.on('project-hover', setProjectHover);
    attachAllIframes();
    startIframeObserver();
  });

  onUnmounted(() => {
    window.removeEventListener('mousemove', updateMouse);
    document.documentElement.removeEventListener('mouseout',  onDocumentMouseOut);
    document.documentElement.removeEventListener('mouseover', onDocumentMouseOver);
    emitter.off('project-hover', setProjectHover);
    detachAllIframes();
    iframeMutationObserver?.disconnect();
    iframeMutationObserver = null;
  });

  const state = {
    x,
    y,
    isHovering,
    isProjectHovering,
    isOverIframe,
    isOutsideWindow,
    currentState,
    containerStyle,
  };

  provide(CURSOR_STATE_KEY, state);

  return state;
}

/**
 * Injects the shared cursor state provided by a parent CursorBase component.
 */
export function useCursorState() {
  return inject(CURSOR_STATE_KEY);
}
